import { Fragment } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { NotificationNavButton } from '../../../Notification/NotificationNavButton';
import type {
  NavHamburgerMenuOption,
  NavAnyExternalLinkItem,
  NavAnyLinkItem,
  NavMenuItem,
  NavPermissionedItem,
} from '../../types';

export type CustomisedNavbarMenuItemProps = {
  item: NavMenuItem;
  disabledAction: boolean;
  firstName: string;
  iconMapper: Record<string, string>;
  checkNavItemVisibility: (item: NavPermissionedItem) => boolean;
  onLinkClick: (item: NavAnyLinkItem) => void;
  navigate: (url: string) => void;
  logout: () => void;
  exitIconSrc: string;
};

export const CustomisedNavbarMenuItem = ({
  item,
  disabledAction,
  firstName,
  iconMapper,
  checkNavItemVisibility,
  onLinkClick,
  navigate,
  logout,
  exitIconSrc,
}: CustomisedNavbarMenuItemProps) => {
  const renderLinkContent = (
    linkItem: NavAnyLinkItem | NavAnyExternalLinkItem,
  ) => {
    if (linkItem.displayType !== 'icon') return linkItem.label;
    const iconKey = (linkItem as { icon: string }).icon;
    return (
      <img
        alt="icon"
        src={iconMapper[iconKey]}
        style={{ width: '20px', height: '20px' }}
      />
    );
  };

  const renderHamburgerOption = (subItem: NavHamburgerMenuOption) => {
    const onClick =
      subItem.type === 'button' && subItem.id === 'logout' ? logout : undefined;

    return (
      <Fragment key={subItem.id}>
        <NavDropdown.Item className="hamburger-menu-item" onClick={onClick}>
          {subItem.type === 'link' ? (
            <a
              href={subItem.url}
              onClick={() => (subItem.url ? navigate(subItem.url) : undefined)}
              className="sub-menu-item"
            >
              {subItem.displayType === 'iconText' ? (
                <>
                  <img
                    src={iconMapper[subItem.icon]}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '7px',
                    }}
                    alt="icon"
                  />
                  {subItem.label}
                </>
              ) : null}
            </a>
          ) : (
            <button className="sub-menu-item">
              {subItem.displayType === 'iconText' ? (
                <>
                  <img
                    src={iconMapper[subItem.icon]}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '7px',
                    }}
                    alt="icon"
                  />
                  {subItem.label}
                </>
              ) : null}
            </button>
          )}
        </NavDropdown.Item>
      </Fragment>
    );
  };

  if (item.type === 'link') {
    const linkItem = item as NavAnyLinkItem;
    return (
      <Nav.Item className="customised-item">
        <Nav.Link
          aria-label={item.label}
          onClick={() => onLinkClick(linkItem)}
          className="nav-item nav-link"
          disabled={disabledAction}
        >
          {renderLinkContent(linkItem)}
          <div className="active-link"></div>
        </Nav.Link>
      </Nav.Item>
    );
  }

  if (item.type === 'external-link') {
    const externalLinkItem = item as NavAnyExternalLinkItem;
    return (
      <Nav.Item className="customised-item">
        <Nav.Link
          aria-label={item.label}
          onClick={() => window.open(externalLinkItem.url, '_blank')}
          className="nav-item nav-link"
          disabled={disabledAction}
        >
          {renderLinkContent(externalLinkItem)}
          <div className="active-link"></div>
        </Nav.Link>
      </Nav.Item>
    );
  }

  if (item.type === 'hamburgerMenu') {
    return (
      <Nav.Item className="customised-item">
        <NavDropdown
          aria-label={item.label}
          title={
            <span>
              <i className="fa-solid fa-bars menu-icon"></i>
            </span>
          }
          align="end"
          className="hamburger-menu-container"
        >
          <div className="hamburger-spacing"></div>
          {item.options
            ?.filter(checkNavItemVisibility)
            .map(renderHamburgerOption)}
        </NavDropdown>
      </Nav.Item>
    );
  }

  if (item.type === 'logout') {
    return (
      <Nav.Item
        className="d-flex flex-column align-items-center justify-content-center"
        onClick={() => logout()}
      >
        <Nav.Link eventKey="link-4">
          <img alt="" src={exitIconSrc} />
        </Nav.Link>
      </Nav.Item>
    );
  }

  if (item.type === 'placeholder' && item.id === 'notification-item') {
    return (
      <Nav.Item className="customised-item">
        <NotificationNavButton />
      </Nav.Item>
    );
  }

  if (item.type === 'placeholder' && item.id === 'my-account') {
    const accountUrl = item.url || '/myaccount';
    const accountIconSrc = item.icon ? iconMapper[item.icon] : '';
    return (
      <Nav.Item className="customised-item">
        <Nav.Link
          aria-label={item.label}
          onClick={() => navigate(accountUrl)}
          disabled={disabledAction}
        >
          {firstName}
          {accountIconSrc ? (
            <img
              src={accountIconSrc}
              style={{
                width: '20px',
                height: '20px',
                marginLeft: '5px',
              }}
              alt="icon"
            />
          ) : null}
        </Nav.Link>
      </Nav.Item>
    );
  }

  return null;
};
